import React, { useRef, useState } from 'react';

import {
  defaultInitialsFieldHeightPx,
  defaultInitialsFieldWidthPx,
  defaultSignatureFieldHeightPx,
  defaultSignatureFieldWidthPx,
  GQL,
  Select,
} from '@aline/common';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import ReactSignatureCanvas from 'react-signature-canvas';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineTooltip } from 'src/components';
import { useMobileSheetActionButtonStyles } from 'src/editor/mobile/signing/mobile-sheet-action-button';
import type { AdoptedSignature } from 'src/layout-engine/components/dialogs/use-adopt-user-signatures';
import { useAdoptUserSignatures } from 'src/layout-engine/components/dialogs/use-adopt-user-signatures';
import { useFramedPreview } from 'src/layout-engine/components/dialogs/use-framed-preview';
import { Sentry } from 'src/sentry';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  adoptButton: {
    marginTop: theme.spacing(2),
  },
  drawArea: {
    backgroundColor: theme.appColors.background,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    height: 140,
    width: '100%',
  },
  drawAreaInitials: {
    backgroundColor: theme.appColors.background,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    height: 100,
    width: '100%',
  },
  drawHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
  },
  inputRow: {
    marginBottom: theme.spacing(1.5),
  },
  preview: {
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    boxSizing: 'border-box',
    display: 'block',
  },
  previewLabel: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  /** Wraps the initials below the signature on screens too narrow for both side by side. */
  previewRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tabs: {
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
}));

interface MobileAdoptAndSignBodyProps {
  onAdopted: (signature: AdoptedSignature, e: React.SyntheticEvent) => Promise<void> | void;
  buttonDisabled?: boolean;
  /**
   * PDF adoption only saves the signature (user taps the field again to stamp),
   * so the dialog passes "Adopt Signature" there. The walkthrough sheet stamps
   * in the same tap, hence the default.
   */
  buttonLabel?: string;
  disabledReason?: string;
}

/**
 * Owns no chrome — the parent decides whether to wrap it in a bottom sheet,
 * dialog, or anything else.
 */
export function MobileAdoptAndSignBody({
  buttonDisabled = false,
  buttonLabel = 'Adopt & Sign',
  disabledReason = '',
  onAdopted,
}: MobileAdoptAndSignBodyProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: actionClasses } = useMobileSheetActionButtonStyles();
  const { adopt, submitting } = useAdoptUserSignatures();
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [typedName, setTypedName] = useState('');
  const [typedInitials, setTypedInitials] = useState('');
  const [drawSigDataUrl, setDrawSigDataUrl] = useState<string | null>(null);
  const [drawInitialsDataUrl, setDrawInitialsDataUrl] = useState<string | null>(null);
  const [sigPreviewCanvas, setSigPreviewCanvas] = useState<HTMLCanvasElement | null>(null);
  const [initialsPreviewCanvas, setInitialsPreviewCanvas] = useState<HTMLCanvasElement | null>(
    null
  );
  const drawSigCanvasRef = useRef<ReactSignatureCanvas | null>(null);
  const drawInitialsCanvasRef = useRef<ReactSignatureCanvas | null>(null);

  const isType = activeTab === 0;
  useFramedPreview({
    canvas: sigPreviewCanvas,
    fallbackText: 'Your signature',
    hPx: defaultSignatureFieldHeightPx,
    textOrDataUrl: isType ? typedName : (drawSigDataUrl ?? ''),
    type: isType || !drawSigDataUrl ? 'text' : 'dataUrl',
    variant: GQL.SignatureFieldVariant.Signature,
    wPx: defaultSignatureFieldWidthPx,
  });
  useFramedPreview({
    canvas: initialsPreviewCanvas,
    fallbackText: 'Initials',
    hPx: defaultInitialsFieldHeightPx,
    textOrDataUrl: isType ? typedInitials : (drawInitialsDataUrl ?? ''),
    type: isType || !drawInitialsDataUrl ? 'text' : 'dataUrl',
    variant: GQL.SignatureFieldVariant.Initials,
    wPx: defaultInitialsFieldWidthPx,
  });

  const isAdoptDisabled =
    submitting ||
    buttonDisabled ||
    (activeTab === 0 && (!typedName || !typedInitials)) ||
    (activeTab === 1 && (!drawSigDataUrl || !drawInitialsDataUrl));

  function clearDrawCanvases(): void {
    drawSigCanvasRef.current?.clear();
    drawInitialsCanvasRef.current?.clear();
    setDrawSigDataUrl(null);
    setDrawInitialsDataUrl(null);
  }

  async function handleAdopt(event: React.MouseEvent): Promise<void> {
    if (!sigPreviewCanvas || !initialsPreviewCanvas || isAdoptDisabled) return;
    const adopted = await adopt({
      initialsCanvas: initialsPreviewCanvas,
      signatureCanvas: sigPreviewCanvas,
    });
    if (adopted) await onAdopted(adopted, event);
  }

  return (
    <>
      <Tabs
        className={classes.tabs}
        value={activeTab}
        variant="fullWidth"
        onChange={(_e, value: 0 | 1) => {
          setActiveTab(value);
          setTypedName('');
          setTypedInitials('');
          clearDrawCanvases();
        }}
      >
        <Tab label="Type" />
        <Tab label="Draw" />
      </Tabs>
      {activeTab === 0 ? (
        <>
          <div className={classes.inputRow}>
            <Typography className={classes.inputLabel}>Your name</Typography>
            <AlineInput
              fullWidth
              data-cy={Select.dialog.signatureDialog.typedSignatureInput}
              inputProps={{ 'aria-label': 'Your name', style: { fontSize: 16 } }}
              placeholder="Enter your full name"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
            />
          </div>
          <div className={classes.inputRow}>
            <Typography className={classes.inputLabel}>Your initials</Typography>
            <AlineInput
              fullWidth
              data-cy={Select.dialog.signatureDialog.typedInitialsInput}
              inputProps={{ 'aria-label': 'Your initials', style: { fontSize: 16 } }}
              placeholder="Initials"
              value={typedInitials}
              onChange={e => setTypedInitials(e.target.value)}
            />
          </div>
        </>
      ) : (
        <>
          <DrawArea
            canvasRef={drawSigCanvasRef}
            className={classes.drawArea}
            headerClassName={classes.drawHeader}
            label="Draw signature"
            labelClassName={classes.inputLabel}
            onChange={dataUrl => setDrawSigDataUrl(dataUrl)}
            onClear={() => {
              drawSigCanvasRef.current?.clear();
              setDrawSigDataUrl(null);
            }}
          />
          <DrawArea
            canvasRef={drawInitialsCanvasRef}
            className={classes.drawAreaInitials}
            headerClassName={classes.drawHeader}
            label="Draw initials"
            labelClassName={classes.inputLabel}
            onChange={dataUrl => setDrawInitialsDataUrl(dataUrl)}
            onClear={() => {
              drawInitialsCanvasRef.current?.clear();
              setDrawInitialsDataUrl(null);
            }}
          />
        </>
      )}
      <Box>
        <Typography className={classes.previewLabel}>Preview</Typography>
        <div className={classes.previewRow}>
          <canvas
            ref={setSigPreviewCanvas}
            aria-label="Signature preview"
            className={classes.preview}
            role="img"
            style={{
              height: defaultSignatureFieldHeightPx,
              width: defaultSignatureFieldWidthPx,
            }}
          />
          <canvas
            ref={setInitialsPreviewCanvas}
            aria-label="Initials preview"
            className={classes.preview}
            role="img"
            style={{
              height: defaultInitialsFieldHeightPx,
              width: defaultInitialsFieldWidthPx,
            }}
          />
        </div>
      </Box>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineButton
            fullWidth
            buttonClassName={actionClasses.button}
            className={classes.adoptButton}
            color="primary"
            data-cy={Select.dialog.submitButton}
            disabled={isAdoptDisabled}
            loading={submitting}
            variant="contained"
            onClick={handleAdopt}
          >
            {buttonLabel}
          </AlineButton>
        </div>
      </AlineTooltip>
    </>
  );
}

function DrawArea({
  canvasRef,
  className,
  headerClassName,
  label,
  labelClassName,
  onChange,
  onClear,
}: {
  canvasRef: React.MutableRefObject<ReactSignatureCanvas | null>;
  className: string;
  headerClassName: string;
  label: string;
  labelClassName: string;
  onChange: (dataUrl: string) => void;
  onClear: () => void;
}): JSX.Element {
  return (
    <>
      <div className={headerClassName}>
        <Typography className={labelClassName}>{label}</Typography>
        <IconButton aria-label="Clear" size="small" onClick={onClear}>
          <Typography variant="caption">Clear</Typography>
        </IconButton>
      </div>
      <ReactSignatureCanvas
        ref={ref => {
          canvasRef.current = ref;
        }}
        canvasProps={{ className }}
        onEnd={() => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          try {
            onChange(canvas.toDataURL('image/png'));
          } catch (err) {
            Sentry.captureException(err);
          }
        }}
      />
    </>
  );
}
